import React from "react";

import cx from "classnames";

import { coalesce } from "@kazoohr/helpers";

import { BRANDING_CLASSES } from "../../styles/branding";
import { Icon, IconDictionary } from "../Icon";
import { Loader } from "../Loader";

import styles from "./Button.scss";

export type ButtonVariant =
  | "basic"
  | "destructive"
  | "destructive_outline"
  | "primary"
  | "primary_branded"
  | "transparent"
  | "system"
  | "secondary"
  | "link"
  | "feature_inverted"
  | "transparentDark"
  | "subtle"
  | "primary_inverted"
  | "pill"
  | "chip";

export type ButtonSize =
  | "default"
  | "extra_small"
  | "small"
  | "large"
  | "extra_large";
export type ButtonTarget = "_self" | "_blank" | "_parent" | "_top";
export type ButtonType = "button" | "submit";
export type IconAlignment = "left" | "right";

export type AsLink = {
  /** URL for the link */
  url: string;
  /** Link element from routing library */
  linkElement: React.ComponentType<any> | string;
};

export interface ButtonProps {
  /** Optional class name for additional styling */
  className?: string;
  /** Disables the button when true */
  disabled?: boolean;
  /** Makes the button take full width of container */
  fullWidth?: boolean;
  /** Reference to the button element */
  innerRef?: React.Ref<HTMLButtonElement | HTMLAnchorElement | undefined>;
  /** Icon name to display */
  icon?: IconDictionary;
  /** Icon variant style */
  iconVariant?: "regular" | "filled" | "thin";
  /** Custom icon color */
  iconColor?: string;
  /** Position of the icon */
  iconAlignment?: IconAlignment;
  /** Custom link element for routing */
  linkElement?: React.ComponentType<any> | "a";
  /** Shows loading state */
  loading?: boolean;
  /** Allow text wrapping */
  wrap?: boolean;
  /** Button size variant */
  size?: ButtonSize;
  /** Button style variant */
  variant?: ButtonVariant;
  /** Target attribute for links */
  target?: ButtonTarget;
  /** Legacy URL prop (deprecated) */
  href?: string;
  /** Legacy routing prop (deprecated) */
  to?: string;
  /** Button type */
  type?: ButtonType;
  /** Link configuration */
  asLink?: AsLink;
  /** Test ID for testing */
  "data-testid"?: string;
  /** On click event for the button */
  onClick?: (event: React.MouseEvent) => unknown;
  /** Button name, optional, useful for certain forms */
  name?: string;
}

function getSizeModifier(size: ButtonSize, fullWidth?: boolean): string {
  return cx({
    [styles["button--extra-small"]]: size === "extra_small",
    [styles["button--small"]]: size === "small",
    [styles["button--large"]]: size === "large",
    [styles["button--extra-large"]]: size === "extra_large",
    [styles["button--full-width"]]: fullWidth,
  });
}

function getIconVariant(
  variant: ButtonVariant,
  iconVariant?: "filled" | "thin" | "regular"
): "filled" | "thin" | "regular" {
  if (iconVariant) {
    return iconVariant;
  }

  return variant === "feature_inverted" ? "filled" : "thin";
}

export function getVariantModifier(variant: ButtonVariant): string {
  switch (variant) {
    case "transparent":
      return styles["button--transparent"];
    case "transparentDark":
      return styles["button--transparent-dark"];
    case "destructive":
      return styles["button--destructive"];
    case "subtle":
      return styles["button--subtle"];
    case "feature_inverted":
      return cx(
        styles["button--feature"],
        styles["button--feature--inverted"],
        BRANDING_CLASSES.BUTTON_FEATURE_INVERTED
      );
    case "primary":
      return cx(styles["button--primary"], BRANDING_CLASSES.BUTTON_PRIMARY);
    case "primary_branded":
      return cx(
        styles["button--primary--branded"],
        BRANDING_CLASSES.BUTTON_PRIMARY_BRANDED
      );
    case "primary_inverted":
      return cx(
        styles["button--primary--inverted"],
        BRANDING_CLASSES.BUTTON_PRIMARY_INVERTED
      );
    case "system":
      return cx(styles["button--system"]);
    case "destructive_outline":
      return cx(styles["button--destructive-outline"]);
    case "pill":
      return cx(styles["button--pill"]);
    case "link":
      return cx(styles["button--link"]);
    case "secondary":
      return cx(styles["button--secondary"], BRANDING_CLASSES.BUTTON_SECONDARY);
    case "chip":
      return cx(styles["button--chip"]);
    case "basic":
    default:
      return styles["button--basic"];
  }
}

interface CustomProps {
  href?: string;
  to?: string;
  target?: ButtonTarget;
  type?: ButtonType;
}

/** Generic button component supporting various styles and states */
export function Button({
  children,
  disabled,
  fullWidth,
  className,
  icon,
  iconColor,
  iconAlignment = "left",
  iconVariant,
  innerRef,
  linkElement = "a",
  loading,
  size = "default",
  variant = "basic",
  href,
  to,
  target,
  "data-testid": dataTestId,
  asLink,
  wrap = false,
  ...elementProps
}: ButtonProps &
  React.HTMLAttributes<
    HTMLButtonElement | HTMLAnchorElement
  >): React.ReactElement {
  const buttonClassName = cx(
    styles.button,
    getSizeModifier(size, fullWidth),
    getVariantModifier(variant),
    { [styles["button--loading"]]: loading },
    className,
    { [styles["button--wrap"]]: wrap }
  );

  const iconVariantButton = getIconVariant(variant, iconVariant);
  const linkTag = getLinkTag(linkElement, asLink);
  const url = getUrl({ href, to, asLink });

  const useLink = url && !disabled;
  const TagName = useLink ? linkTag : "button";
  const props: CustomProps = { ...elementProps };

  if (useLink) {
    const isAnchor = linkTag === "a";
    props[isAnchor ? "href" : "to"] = url;
    if (target) {
      props.target = target;
    }
  }

  const iconWrapperStyles = cx(styles["button--icon-wrapper"], {
    [BRANDING_CLASSES.BUTTON_FEATURE_INVERTED_ICON_WRAPPER]:
      variant === "feature_inverted",
    [styles["button--icon-wrapper--left"]]: iconAlignment === "left",
    [styles["button--icon-wrapper--right"]]: iconAlignment === "right",
  });

  return (
    <TagName
      {...props}
      className={buttonClassName}
      disabled={disabled || loading}
      data-testid={dataTestId}
      ref={innerRef}
      type={TagName === "button" ? props.type : undefined}
    >
      {iconAlignment === "right" && children}
      {icon && (
        <div
          className={iconWrapperStyles}
          data-testid={`${coalesce(dataTestId, "button")}-icon-wrapper`}
        >
          <Icon
            data-testid={`${coalesce(dataTestId, "button")}-icon-${icon}`}
            icon={icon}
            color={coalesce(iconColor, "currentColor")}
            variant={iconVariantButton}
          />
        </div>
      )}
      {iconAlignment === "left" && children}
      {loading && (
        <div
          className={styles["button--loader-wrapper"]}
          data-testid={`${coalesce(dataTestId, "button")}-loader-div`}
        >
          <Loader size="small" />
        </div>
      )}
    </TagName>
  );
}

interface UrlProps {
  href?: string;
  to?: string;
  asLink?: AsLink;
}

export function getUrl({ href, to, asLink }: UrlProps): string | undefined {
  if (href) {
    return href;
  }
  if (to) {
    return to;
  }
  if (asLink?.url) {
    return asLink.url;
  }
  return undefined;
}

export function getLinkTag(
  linkElement: React.ComponentType<any> | string,
  asLink?: AsLink
): React.ComponentType<any> | string {
  if (asLink?.linkElement) {
    return asLink.linkElement;
  }
  return linkElement;
}
